import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { kycService, mandateService } from "../../services/api";
import toast from "react-hot-toast";
import AuthHero from "../../components/auth/AuthHero";
import { logo } from "../../constants/icons";

const PlayerMandateKycStart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token: paramToken } = useParams();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const queryToken = searchParams.get("invite_token");
  const queryPlayerId = searchParams.get("player_id");
  const queryMobile = searchParams.get("mobile");

  const {
    playerId: statePlayerId,
    mobile: stateMobile,
    token: stateToken,
    verification: stateVerification,
    inviteParams,
    rawQuery,
    email,
    inviteSentBy,
    senderType,
    agencyId,
    type,
  } = location.state || {};

  const mandateToken = stateToken || paramToken || queryToken || null;
  const [playerId, setPlayerId] = useState(
    statePlayerId ?? (queryPlayerId ? Number(queryPlayerId) : null),
  );
  const [wyscoutId, setWyscoutId] = useState(null);
  const [mobile, setMobile] = useState(
    (stateMobile || queryMobile || "").trim(),
  );
  const [loading, setLoading] = useState(false);
  const [fetchingMandate, setFetchingMandate] = useState(false);
  const [verificationInfo, setVerificationInfo] = useState(
    stateVerification || null,
  );
  const [mandateError, setMandateError] = useState("");
  const [hasFetchedDetails, setHasFetchedDetails] = useState(false);

  const basePath = useMemo(() => {
    if (mandateToken) {
      return `/player-register-mandate/${mandateToken}`;
    }
    return "/player-register-mandate";
  }, [mandateToken]);

  const navigationState = {
    token: mandateToken,
    playerId,
    mobile,
    verification: verificationInfo,
    inviteParams,
    rawQuery,
    email,
    inviteSentBy,
    senderType,
    agencyId,
    type,
  };

  useEffect(() => {
    if (!mandateToken) {
      toast.error(
        "Invalid or expired invitation. Please reopen your invite link.",
      );
      navigate("/player-register-mandate", { replace: true });
    }
  }, [mandateToken, navigate]);

  useEffect(() => {
    setHasFetchedDetails(false);
  }, [mandateToken]);

  useEffect(() => {
    if (!mandateToken || hasFetchedDetails) {
      return;
    }

    const shouldFetchDetails = !playerId || !verificationInfo || !mobile;

    if (!shouldFetchDetails) {
      setHasFetchedDetails(true);
      return;
    }

    setHasFetchedDetails(true);

    const fetchMandateDetails = async () => {
      setFetchingMandate(true);
      setMandateError("");
      try {
        const response =
          await mandateService.getMandateDetailsByToken(mandateToken);

        if (!response.success) {
          throw new Error(
            response.error || "Failed to load invitation details",
          );
        }

        const data = response.data;

        if (!playerId && data?.mandate?.player_id) {
          setPlayerId(Number(data.mandate.player_id));
        }

        // Extract wyscout_id for "continue without signup" flow
        if (data?.wyscout_id) {
          setWyscoutId(Number(data.wyscout_id));
        }

        if (!mobile && data?.verification?.mobile) {
          setMobile(String(data.verification.mobile).trim());
        }

        if (data?.verification) {
          setVerificationInfo(data.verification);
        }
      } catch (error) {
        console.error("Fetch mandate details error:", error);
        const message = error.message || "Failed to load invitation details";
        setMandateError(message);
        toast.error(message);
      } finally {
        setFetchingMandate(false);
      }
    };

    fetchMandateDetails();
  }, [mandateToken, playerId, verificationInfo, mobile, hasFetchedDetails]);

  const requiresFreshVerification = useMemo(() => {
    if (!verificationInfo) {
      return true;
    }

    if (verificationInfo.reverify_required) {
      return true;
    }

    return !verificationInfo.id_verified;
  }, [verificationInfo]);

  const startVerificationFlow = async () => {
    if (loading || fetchingMandate) {
      return;
    }

    if (!mandateToken) {
      toast.error(
        "Missing representation information. Please reopen your invite link.",
      );
      return;
    }

    // Either playerId or wyscoutId is required
    if (!playerId && !wyscoutId) {
      toast.error(
        "Unable to identify your player profile. Please reopen your invite link.",
      );
      return;
    }

    if (!requiresFreshVerification) {
      const actionPath = `${basePath}/action`;
      navigate(actionPath, {
        state: {
          ...navigationState,
          playerId,
          wyscoutId,
        },
      });
      return;
    }

    const sanitizedMobile = (mobile || "").trim();
    setLoading(true);
    try {
      const payload = sanitizedMobile ? { mobile: sanitizedMobile } : {};
      const res = await kycService.getPublicPlayerVerificationLink(
        playerId,
        payload
      );

      // Check if response is successful
      if (res?.success) {
        // Check if biometric verification endpoints are provided (new flow)
        if (res?.data?.biometric_start_endpoint || res?.data?.verification_type === 'biometric' || res?.results?.biometric_start_endpoint) {
          // Navigate to biometric verification flow
          const biometricData = res?.data || res?.results || {};
          const biometricPath = `${basePath}/biometric-verification`;
          navigate(biometricPath, {
            state: {
              ...navigationState,
              playerId,
              mobile: sanitizedMobile || null,
              verification: res.data?.verification || biometricData?.verification || verificationInfo,
              biometricEndpoints: biometricData,
              profilePhotoUrl: biometricData?.profile_photo_url,
            },
          });
        } 
        // Check if DocuPass URL is provided (legacy flow)
        else if (res?.data?.url) {
          const kycPath = `${basePath}/kyc`;
          navigate(kycPath, {
            state: {
              ...navigationState,
              documentUrl: res.data.url,
              mobile: sanitizedMobile || null,
              playerId,
              verification: res.data?.verification || verificationInfo,
            },
          });
        } 
        // If neither biometric nor DocuPass, show error
        else {
          toast.error(
            res?.data?.message || res?.message || "Verification endpoints not available"
          );
        }
      } else {
        toast.error(
          res?.error || res?.message || res?.data?.message || "Failed to get verification link"
        );
      }
    } catch (error) {
      console.error("Failed to start verification:", error);
      toast.error("Failed to get verification link");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(basePath, {
      state: {
        ...navigationState,
        playerId,
        wyscoutId,
        mobile,
      },
    });
  };

  // Button is enabled if we have either playerId or wyscoutId
  const buttonDisabled =
    !mandateToken || loading || fetchingMandate || (!playerId && !wyscoutId);

  // Debug: Log why button might be disabled
  useEffect(() => {
    if (buttonDisabled) {
      console.log("Button disabled because:", {
        noMandateToken: !mandateToken,
        loading,
        fetchingMandate,
        noPlayerId: !playerId,
        noWyscoutId: !wyscoutId,
        playerId,
        wyscoutId,
        mandateToken,
      });
    }
  }, [buttonDisabled, mandateToken, loading, fetchingMandate, playerId, wyscoutId]);

  return (
    <div className="flex min-h-screen">
      {/* <AuthHero /> */}
      <div className="flex-1 bg-white flex items-center justify-center">
        <div className="w-full max-w-xl p-6 border border-gray-400 rounded-xl">
            <div className="flex items-center mb-6">
              <button
                onClick={handleBack}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5 mr-2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 19.5L8.25 12l7.5-7.5"
                  />
                </svg>
                Back
              </button>
            </div>

            <div className="text-center">
              <img src={logo} alt="Logo" className="mx-auto mb-4 h-10" />

              <h2 className="text-[24px] leading-[32px] font-semibold text-gray-900 mb-3">
                Identity Verification Required
              </h2>
              <p className="text-[16px] leading-[24px] text-gray-600 mb-8">
                To proceed with your representation invitation, we need to verify your
                identity for security purposes.
              </p>

              {mandateError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm font-medium">{mandateError}</p>
                  <p className="text-xs mt-1">
                    Please refresh the page or reopen the invitation email.
                  </p>
                </div>
              )}

              {fetchingMandate && (
                <div className="flex justify-center mb-6">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
                </div>
              )}

              {requiresFreshVerification && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 text-left">
                  <div className="flex items-center gap-3 mb-3">
                    <svg
                      className="w-6 h-6 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <h3 className="text-lg font-medium text-blue-900">
                      Quick Face Verification
                    </h3>
                  </div>
                  <ul className="text-blue-800 space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Take a quick selfie to verify your identity
                    </li>
                    <li className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Good lighting and camera access needed
                    </li>
                    <li className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Takes less than 30 seconds
                    </li>
                  </ul>
                </div>
              )}

              {!requiresFreshVerification && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left mb-6">
                  <h3 className="text-green-800 font-medium mb-1">
                    Identity already verified
                  </h3>
                  <p className="text-sm text-green-700">
                    We found a recent verification on file. Continue to review
                    the representation details and accept the invite.
                  </p>
                </div>
              )}

              <button
                onClick={startVerificationFlow}
                disabled={buttonDisabled}
                className={`w-full py-4 px-6 rounded-lg transition-colors font-medium text-[16px] flex items-center justify-center gap-2 ${
                  buttonDisabled
                    ? "bg-blue-400 text-white cursor-not-allowed"
                    : "bg-[#2283FF] hover:bg-[#1a6cd4] text-white"
                }`}
              >
                {(loading || fetchingMandate) && (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                )}
                {requiresFreshVerification
                  ? loading
                    ? "Getting verification link..."
                    : fetchingMandate
                    ? "Loading..."
                    : "Start Verification"
                  : "Continue"}
              </button>

              {/* Show reason if button is disabled */}
              {buttonDisabled && !loading && !fetchingMandate && (
                <p className="text-sm text-amber-600 mt-2 text-center">
                  {!mandateToken
                    ? "Invalid invitation link. Please reopen from your email."
                    : !playerId
                    ? "Loading player information..."
                    : "Please wait..."}
                </p>
              )}

              <p className="text-center text-sm text-gray-500 mt-6">
                This verification is secure and your data is protected according
                to our Privacy Policy
              </p>
            </div>
          </div>
      </div>
    </div>
  );
};

export default PlayerMandateKycStart;
